/**
 * Portfolio-links met een preview voor YouTube, Vimeo en Behance. Pas na een klik wordt de speler/embed
 * (en daarmee code van die partij) geladen; vooraf alleen een stille kaart (YouTube: een thumbnail-plaatje).
 * Andere links blijven gewone links.
 */
import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LanguageIcon from '@mui/icons-material/Language';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import BrushOutlinedIcon from '@mui/icons-material/BrushOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { ACCENT, INKT, KOLOM_BG, LIJN, STIL, portfolioEmbed, portfolioSoort } from './wervingLogica';

// Herkenbaar icoon per soort link, in de merkkleur (alleen het icoon; de tekst blijft rustig).
const ICOON = {
  linkedin: [LinkedInIcon, '#0a66c2'], youtube: [YouTubeIcon, '#d62020'], vimeo: [PlayCircleOutlineIcon, '#1a8fb5'],
  behance: [BrushOutlinedIcon, '#1f5fd6'], profiel: [PersonOutlineIcon, ACCENT], website: [LanguageIcon, ACCENT],
};

function Icoon({ url }) {
  const [C, kleur] = ICOON[portfolioSoort(url).soort] || ICOON.website;
  return <C sx={{ fontSize: 18, color: kleur }} />;
}

function Preview({ url, e }) {
  const [geladen, setGeladen] = useState(false);
  return (
    <Box sx={{ border: `1px solid ${LIJN}`, borderRadius: 3, overflow: 'hidden', bgcolor: '#fff', mb: 1.5, maxWidth: 640 }}>
      <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', bgcolor: KOLOM_BG }}>
        {geladen ? (
          <Box component="iframe" src={e.embed} title={`${e.label}-preview`} loading="lazy" referrerPolicy="no-referrer"
            allow="autoplay; fullscreen; picture-in-picture" allowFullScreen
            sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }} />
        ) : (
          <Box role="button" tabIndex={0} onClick={() => setGeladen(true)} onKeyDown={(ev) => { if (ev.key === 'Enter') setGeladen(true); }}
            sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', cursor: 'pointer' }}>
            {e.thumbnail && (
              <Box component="img" src={e.thumbnail} alt="" referrerPolicy="no-referrer" loading="lazy"
                sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
            )}
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: 'rgba(255,255,255,.92)',
              borderRadius: 10, px: 1.5, py: 0.75, color: INKT, fontSize: 13.5, fontWeight: 600 }}>
              <PlayArrowRoundedIcon fontSize="small" /> {e.soort === 'behance' ? 'Project bekijken' : 'Afspelen'}
            </Box>
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 0.75, gap: 1 }}>
        <Icoon url={url} />
        <Typography sx={{ fontSize: 12.5, color: STIL, flex: 1 }}>{e.label}</Typography>
        <Button size="small" href={url} target="_blank" rel="noopener noreferrer" sx={{ textTransform: 'none', color: ACCENT }}>Origineel openen</Button>
      </Box>
    </Box>
  );
}

export default function Portfolio({ urls }) {
  return (
    <Box>
      {(urls || []).map((u) => {
        const e = portfolioEmbed(u);
        return e ? <Preview key={u} url={u} e={e} /> : (
          <Box key={u} component="a" href={u} target="_blank" rel="noopener noreferrer" title={u}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 14, color: ACCENT, mt: 0.5, mb: 1, textDecoration: 'none',
              '&:hover span': { textDecoration: 'underline' } }}>
            <Icoon url={u} /><Box component="span" sx={{ wordBreak: 'break-all' }}>{portfolioSoort(u).label}</Box>
          </Box>
        );
      })}
    </Box>
  );
}
